import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { getImageCacheKey, getImageUrl, invalidateImageCache } from '../../../lib/helpers';
import { isValidEmail } from '../../../lib/email';
import { DEFAULT_PHONE_COUNTRY, countryFromPhone, isValidSocioPhone } from '../../../lib/phone';
import { toIsoDateOrFallback, isIncompleteDate } from '../../../lib/dates';
import CustomModal from '../../Elements/CustomModal';
import { scanDni } from '../../../lib/dniScanner';
import SignaturePad from './SignaturePad';

// ── Duration helpers ────────────────────────────────────────────────────────
const DEFAULT_DURATIONS = [
  { id: '__1m', label: '1 Mes',   months: 1  },
  { id: '__3m', label: '3 Meses', months: 3  },
  { id: '__1y', label: '1 Año',   months: 12 },
];

function addMonths(date, n) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
}

/** Preset cuyo cálculo inscripción + N meses coincide con la caducidad (misma lógica que los botones). */
function findMatchingDurationPresetId(enrollmentIso, expirationIso, durationList) {
  if (!enrollmentIso || !expirationIso || !durationList?.length) return null;
  const [y, m, d] = enrollmentIso.split('-').map(Number);
  const enr = new Date(y, m - 1, d);
  for (const preset of durationList) {
    const end = addMonths(new Date(enr), preset.months);
    const iso = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
    if (iso === expirationIso) return preset.id;
  }
  return null;
}
function toISO(date) {
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
}
function toDisplay(date) {
  return `${String(date.getDate()).padStart(2,'0')}/${String(date.getMonth()+1).padStart(2,'0')}/${date.getFullYear()}`;
}

/** Alineado con backend: NIF, NIE, pasaporte extranjero (hasta 20 caracteres). */
function normalizeNationalIdInput(raw) {
  const s = String(raw ?? '').trim().toUpperCase().replace(/\s+/g, '');
  return s || null;
}

/** Caducidad un año después de la inscripción (ISO); el backend exige caducidad > inscripción. */
function defaultExpirationAfterEnrollment(enrollmentIso) {
  const [y, m, d] = enrollmentIso.split('-').map(Number);
  const end = new Date(y, m - 1, d);
  end.setFullYear(end.getFullYear() + 1);
  return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
}

function getClientProfileImagePath(profile) {
  return profile?.documents_path || profile?.image_path || null;
}

// ── DateField — reusable date text+picker combo ─────────────────────────────
function DateField({ label, value, onChange, pickerValue, onPickerChange, placeholder = 'dd/mm/aaaa' }) {
  return (
    <div className="wa-form-field">
      <label className="wa-label">{label}</label>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => {
            let v = e.target.value.replace(/[^\d/]/g, '');
            if (v.length <= 10) {
              const nums = v.replace(/\//g, '');
              let fmt = '';
              for (let i = 0; i < nums.length; i++) {
                if (i === 2 || i === 4) fmt += '/';
                fmt += nums[i];
              }
              onChange(fmt);
              if (fmt.length === 10 && /^\d{2}\/\d{2}\/\d{4}$/.test(fmt)) {
                const [d, m, y] = fmt.split('/');
                onPickerChange(`${y}-${m}-${d}`);
              }
            }
          }}
          placeholder={placeholder}
          maxLength={10}
          className="wa-input pr-10"
        />
        <input
          type="date"
          value={pickerValue}
          onChange={(e) => {
            if (e.target.value) {
              const [y, m, d] = e.target.value.split('-');
              onChange(`${d}/${m}/${y}`);
              onPickerChange(e.target.value);
            }
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 opacity-0 cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-base" aria-hidden>📅</span>
      </div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
function CustomerModal({ active, handleModal, token, id, clientData, prefillData, shopName, shopId, setErrorMessage, setShowNuevaEntradaModal, onClientCreated, onClientUpdated }) {
  const navigate = useNavigate();
  const isPreRegistrationFlow = !id && !!prefillData?.preRegistrationId;
  // Core fields
  const [email, setEmail]                             = useState('');
  const [image, setImage]                             = useState(null);
  const [signatureDataUrl, setSignatureDataUrl]       = useState(null);
  const [nombreSocioInput, setNombreSocioInput]       = useState('');
  const [apellidoSocioInput, setApellidoSocioInput]   = useState('');
  const [dniSocioInput, setDniSocioInput]             = useState('');
  const [fechaNacimientoSocioInput, setFechaNacimientoSocioInput] = useState('');
  const [datePickerValue, setDatePickerValue]         = useState('');
  const [inscripcionInput, setInscripcionInput]       = useState('');
  const [datePickerInscripcion, setDatePickerInscripcion] = useState('');
  const [caducidadInput, setCaducidadInput]           = useState('');
  const [datePickerCaducidad, setDatePickerCaducidad] = useState('');
  const [telefonoSocioInput, setTelefonoSocioInput]   = useState('');   // E.164 (+34...) o ''
  const [telefonoPaisInput, setTelefonoPaisInput]     = useState(DEFAULT_PHONE_COUNTRY); // ISO-2 del selector
  const [direccionSocioInput, setDireccionSocioInput] = useState('');
  // Extra fields
  const [socioAvaladoInput, setSocioAvaladoInput]     = useState('');
  const [lineaCosteInput, setLineaCosteInput]         = useState('Defecto');
  const [descuentoInput, setDescuentoInput]           = useState('Sin descuento');
  const [cuotaInput, setCuotaInput]                   = useState('');
  const [consumoMaxInput, setConsumoMaxInput]         = useState('');
  const [consumptionTypeInput, setConsumptionTypeInput] = useState(null);
  const [cpSocioInput, setCpSocioInput]               = useState('');
  const [ciudadSocioInput, setCiudadSocioInput]       = useState('');
  const [notesInput, setNotesInput]                   = useState('');
  // Internal Nº socio: previewed next number (gap-fill) shown on create, editable as an override.
  const [membershipInput, setMembershipInput]         = useState('');
  const [nextMembershipId, setNextMembershipId]       = useState(null);
  // UI state
  const [localError, setLocalError]   = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [sociosCap, setSociosCap]     = useState(null);
  // Cuota de inscripción cobrada automáticamente al alta (configurada por venue).
  const [inscriptionFee, setInscriptionFee] = useState(null);
  const [chargeInscriptionFee, setChargeInscriptionFee] = useState(true);
  // Cuota de inscripción: siempre efectivo (va a la caja). Se mantiene el valor por si
  // el backend lo espera; ya no hay selector de tarjeta.
  const [feePaymentMethod] = useState('cash');
  const [durations, setDurations]     = useState(DEFAULT_DURATIONS);
  const [selectedDurationId, setSelectedDurationId] = useState(null);
  // Camera (member photo)
  const [cameraOpen, setCameraOpen]   = useState(false);
  const [cameraError, setCameraError] = useState('');
  // DNI scanner
  const [dniScanError, setDniScanError]     = useState('');
  const [dniScanning, setDniScanning]       = useState(false);
  const [dniPartial, setDniPartial]         = useState(null); // data collected from front
  const [dniFrontFile, setDniFrontFile]     = useState(null);
  const [dniBackFile, setDniBackFile]       = useState(null);
  const [dniCameraOpen, setDniCameraOpen]   = useState(false);
  const [dniCameraError, setDniCameraError] = useState('');
  const [dniCameraSide, setDniCameraSide]   = useState('front');
  // Pre-registration DNI previews (blob URLs, fetched with auth)
  const [dniPrefillFrontUrl, setDniPrefillFrontUrl] = useState(null);
  const [dniPrefillBackUrl, setDniPrefillBackUrl]   = useState(null);
  const [dniExistingFrontUrl, setDniExistingFrontUrl] = useState(null);
  const [dniExistingBackUrl, setDniExistingBackUrl]   = useState(null);
  const [dniNewFrontPreviewUrl, setDniNewFrontPreviewUrl] = useState(null);
  const [dniNewBackPreviewUrl, setDniNewBackPreviewUrl]   = useState(null);

  const lastLoadedIdRef  = useRef(null);
  const galleryInputRef  = useRef(null);
  const cameraInputRef   = useRef(null);
  const dniCamFrontInputRef  = useRef(null);
  const dniCamBackInputRef   = useRef(null);
  const dniGalleryFrontRef   = useRef(null);
  const dniGalleryBackRef    = useRef(null);
  const videoRef         = useRef(null);
  const canvasRef        = useRef(null);
  const mediaStreamRef   = useRef(null);
  const dniVideoRef      = useRef(null);
  const dniCanvasRef     = useRef(null);
  const dniMediaStreamRef = useRef(null);

  // ── Camera helpers ─────────────────────────────────────────────────────
  const stopStream = (ref) => {
    if (ref.current) { ref.current.getTracks().forEach((t) => t.stop()); ref.current = null; }
  };

  // ── Fetch duration presets (crear y editar: botones 1m / 3m / 1a en edición) ──
  useEffect(() => {
    if (!active || !shopId || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/v1/venues/${shopId}/membership-durations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data.length) setDurations(data);
      } catch { /* keep defaults */ }
    })();
    return () => { cancelled = true; };
  }, [active, shopId, token]);

  // ── Fetch venue cap ────────────────────────────────────────────────────
  useEffect(() => {
    if (!active || id || !shopId || !token) { setSociosCap(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/v1/venues/${shopId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && (data.max_socios != null || data.socios_count != null)) {
          setSociosCap({ socios_count: data.socios_count ?? 0, max_socios: data.max_socios ?? null });
        }
        if (!cancelled) setInscriptionFee(data.inscription_fee != null ? Number(data.inscription_fee) : null);
      } catch { if (!cancelled) setSociosCap(null); }
    })();
    return () => { cancelled = true; };
  }, [active, id, shopId, token]);

  // Preview the next internal Nº socio for the create form (editable override).
  // danimateu shows this on its create screen; we mirror it. It's a hint, not a
  // reservation — the real number is assigned at create time.
  useEffect(() => {
    if (!active || id || !shopId || !token) { setNextMembershipId(null); setMembershipInput(''); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/v1/clients/next-membership-id/${shopId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const next = res?.data?.next_membership_id;
        if (!cancelled && next != null) {
          setNextMembershipId(next);
          setMembershipInput(String(next));
        }
      } catch { /* preview is only a hint; ignore failures */ }
    })();
    return () => { cancelled = true; };
  }, [active, id, shopId, token]);

  // ── Load existing client data ──────────────────────────────────────────
  useEffect(() => {
    if (active && id && clientData) {
      if (lastLoadedIdRef.current === id) return;
      lastLoadedIdRef.current = id;

      const parseDate = (raw) => {
        if (!raw) return { display: '', iso: '' };
        const s = String(raw).split('T')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
          const [y, m, d] = s.split('-');
          return { display: `${d}/${m}/${y}`, iso: s };
        }
        return { display: '', iso: '' };
      };

      setEmail(clientData.email || '');
      setNombreSocioInput(clientData.profile?.first_name || '');
      setApellidoSocioInput(clientData.profile?.last_name || '');
      setDniSocioInput(clientData.profile?.national_id?.toString() || '');

      const bd = parseDate(clientData.profile?.birth_date);
      setFechaNacimientoSocioInput(bd.display); setDatePickerValue(bd.iso);

      const enr = parseDate(clientData.profile?.enrollment_date);
      setInscripcionInput(enr.display); setDatePickerInscripcion(enr.iso);

      const exp = parseDate(clientData.profile?.expiration_date);
      setCaducidadInput(exp.display); setDatePickerCaducidad(exp.iso);

      const initialPhone = clientData.profile?.phone?.toString() || '';
      setTelefonoSocioInput(initialPhone);
      setTelefonoPaisInput(
        clientData.profile?.phone_country || countryFromPhone(initialPhone) || DEFAULT_PHONE_COUNTRY
      );
      setDireccionSocioInput(clientData.profile?.address || '');
      setSocioAvaladoInput(clientData.profile?.guarantor_member || '');
      setLineaCosteInput(clientData.profile?.cost_line || 'Defecto');
      setDescuentoInput(clientData.profile?.discount || 'Sin descuento');
      {
        const raw = String(clientData.profile?.fee ?? '').trim();
        if (!raw) setCuotaInput('');
        else {
          const compact = raw.replace(/\s/g, '');
          const legacy = /^0[,.]00€?$/.test(compact) || raw === '0.00 €' || raw === '0,00 €';
          setCuotaInput(legacy ? '' : raw);
        }
      }
      setConsumoMaxInput(clientData.profile?.max_monthly_consumption?.toString() || '');
      const ludenos = clientData.profile?.consumption_type?.ludenos || false;
      const terap   = clientData.profile?.consumption_type?.terap   || false;
      setConsumptionTypeInput(ludenos ? 'ludenos' : terap ? 'terap' : null);
      setCpSocioInput(clientData.profile?.postal_code?.toString() || '');
      setCiudadSocioInput(clientData.profile?.city || '');
      setNotesInput(clientData.profile?.notes || '');
      const profileImagePath = getClientProfileImagePath(clientData.profile);
      if (profileImagePath) {
        setCurrentImageUrl(getImageUrl(profileImagePath, {
          cacheKey: getImageCacheKey('client', clientData.id || id),
        }));
      } else setCurrentImageUrl(null);
    } else if (!active) {
      lastLoadedIdRef.current = null;
    }

    if (active && !id) {
      // Reset create form
      setEmail(''); setImage(null);
      setNombreSocioInput(''); setApellidoSocioInput(''); setDniSocioInput('');
      setFechaNacimientoSocioInput(''); setDatePickerValue('');
      // Default inscription date to today so duration buttons work immediately on open
      const _today = new Date();
      setInscripcionInput(toDisplay(_today)); setDatePickerInscripcion(toISO(_today));
      setCaducidadInput(''); setDatePickerCaducidad('');
      setTelefonoSocioInput(''); setTelefonoPaisInput(DEFAULT_PHONE_COUNTRY); setDireccionSocioInput('');
      setSocioAvaladoInput(''); setLineaCosteInput('Defecto');
      setDescuentoInput('Sin descuento'); setCuotaInput('');
      setDniFrontFile(null); setDniBackFile(null); setDniPartial(null);
      setSignatureDataUrl(null);
      // Apply pre-fill data from pre-registration approval if provided
      if (prefillData) {
        if (prefillData.nombre)    setNombreSocioInput(prefillData.nombre);
        if (prefillData.apellidos) setApellidoSocioInput(prefillData.apellidos);
        if (prefillData.dni)       setDniSocioInput(prefillData.dni);
        if (prefillData.telefono) {
          setTelefonoSocioInput(prefillData.telefono);
          setTelefonoPaisInput(countryFromPhone(prefillData.telefono) || DEFAULT_PHONE_COUNTRY);
        }
        if (prefillData.email)     setEmail(prefillData.email);
        if (prefillData.direccion) setDireccionSocioInput(prefillData.direccion);
        if (prefillData.fecha_nacimiento) {
          setFechaNacimientoSocioInput(prefillData.fecha_nacimiento);
          const [d, m, y] = prefillData.fecha_nacimiento.split('/');
          setDatePickerValue(`${y}-${m}-${d}`);
        }
        if (prefillData.caducidad) {
          setCaducidadInput(prefillData.caducidad);
          const [d, m, y] = prefillData.caducidad.split('/');
          setDatePickerCaducidad(`${y}-${m}-${d}`);
        }
      }
      setConsumoMaxInput(''); setConsumptionTypeInput(null);
      setCpSocioInput(''); setCiudadSocioInput('');
      setNotesInput(prefillData?.notes || '');
      // photo_path is now an auth-protected proxy path; resolved via effect below
      setCurrentImageUrl(null); setSelectedDurationId(null);
    }
  }, [active, id, clientData, prefillData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pre-registration photo: fetch with auth token → blob URL ──────────────
  useEffect(() => {
    const path = prefillData?.photo_path;
    if (!active || id || !path || !token) return;

    // Absolute URL (old presigned S3) — use directly
    if (path.startsWith('http://') || path.startsWith('https://')) {
      setCurrentImageUrl(path);
      return;
    }

    // Proxy path — must fetch with auth header
    let blobUrl = null;
    let cancelled = false;
    const apiBase = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
    const url = `${apiBase}${path.startsWith('/') ? path : `/${path}`}`;

    (async () => {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        if (cancelled || !blob.size) return;
        blobUrl = URL.createObjectURL(blob);
        setCurrentImageUrl(blobUrl);
      } catch { /* leave null */ }
    })();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [active, id, prefillData?.photo_path, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pre-registration DNI previews: fetch front + back with auth ──────────
  useEffect(() => {
    const makeFetcher = (path, setter) => {
      if (!active || id || !path || !token) { setter(null); return; }
      if (path.startsWith('http://') || path.startsWith('https://')) { setter(path); return; }
      let blobUrl = null;
      let cancelled = false;
      const apiBase = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      const url = `${apiBase}${path.startsWith('/') ? path : `/${path}`}`;
      (async () => {
        try {
          const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
          if (!res.ok || cancelled) return;
          const blob = await res.blob();
          if (cancelled || !blob.size) return;
          blobUrl = URL.createObjectURL(blob);
          setter(blobUrl);
        } catch { /* leave null */ }
      })();
      return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
    };

    const cleanFront = makeFetcher(prefillData?.dni_front_path, setDniPrefillFrontUrl);
    const cleanBack  = makeFetcher(prefillData?.dni_back_path,  setDniPrefillBackUrl);
    return () => { cleanFront?.(); cleanBack?.(); };
  }, [active, id, prefillData?.dni_front_path, prefillData?.dni_back_path, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Edit mode: fetch existing DNI scans with auth ───────────────────────
  useEffect(() => {
    if (!active || !id || !token) {
      setDniExistingFrontUrl(null);
      setDniExistingBackUrl(null);
      return undefined;
    }

    let cancelled = false;
    const blobUrls = [];

    const fetchSide = async (side, setter) => {
      try {
        const res = await fetch(`/v1/clients/${id}/dni/${side}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled || !res.ok) {
          if (!cancelled) setter(null);
          return;
        }
        const blob = await res.blob();
        if (cancelled || !blob.size) {
          if (!cancelled) setter(null);
          return;
        }
        const blobUrl = URL.createObjectURL(blob);
        blobUrls.push(blobUrl);
        setter(blobUrl);
      } catch {
        if (!cancelled) setter(null);
      }
    };

    fetchSide('front', setDniExistingFrontUrl);
    fetchSide('back', setDniExistingBackUrl);

    return () => {
      cancelled = true;
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [active, id, token]);

  useEffect(() => {
    if (!dniFrontFile) { setDniNewFrontPreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(dniFrontFile);
    setDniNewFrontPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [dniFrontFile]);

  useEffect(() => {
    if (!dniBackFile) { setDniNewBackPreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(dniBackFile);
    setDniNewBackPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [dniBackFile]);

  // ── Edición: marcar el botón de duración al abrir según fechas guardadas (y al cargar presets del venue) ──
  useEffect(() => {
    if (!active || !id || !clientData?.profile || !durations.length) return;
    const toDayIso = (raw) => {
      if (!raw) return '';
      const s = String(raw).split('T')[0];
      return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
    };
    const enroll = toDayIso(clientData.profile.enrollment_date);
    const expire = toDayIso(clientData.profile.expiration_date);
    setSelectedDurationId(findMatchingDurationPresetId(enroll, expire, durations));
  }, [active, id, clientData, durations]);

  // Stop camera streams on modal close
  useEffect(() => {
    if (!active) {
      stopStream(mediaStreamRef); setCameraOpen(false); setCameraError('');
      stopStream(dniMediaStreamRef); setDniCameraOpen(false); setDniCameraError('');
      setDniScanError(''); setDniPartial(null); setDniFrontFile(null); setDniBackFile(null);
      setSignatureDataUrl(null);
    }
  }, [active]);

  // ── Member photo camera ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const open = async () => {
      if (!cameraOpen) { stopStream(mediaStreamRef); return; }
      setCameraError('');
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError('Tu navegador no permite acceder a la cámara directamente.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        mediaStreamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
      } catch { if (!cancelled) setCameraError('No se pudo acceder a la cámara. Revisa permisos.'); }
    };
    open();
    return () => { cancelled = true; stopStream(mediaStreamRef); };
  }, [cameraOpen]);

  // ── DNI camera ────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const open = async () => {
      if (!dniCameraOpen) { stopStream(dniMediaStreamRef); return; }
      setDniCameraError('');
      if (!navigator.mediaDevices?.getUserMedia) {
        setDniCameraError('Tu navegador no permite acceder a la cámara directamente.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
          },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        dniMediaStreamRef.current = stream;
        if (dniVideoRef.current) {
          dniVideoRef.current.srcObject = stream;
          await dniVideoRef.current.play().catch(() => {});
        }
      } catch {
        if (!cancelled) {
          setDniCameraError('No se pudo acceder a la cámara trasera. Puedes usar el selector del dispositivo.');
        }
      }
    };
    open();
    return () => { cancelled = true; stopStream(dniMediaStreamRef); };
  }, [dniCameraOpen]);

  // ── DNI scan logic ─────────────────────────────────────────────────────
  const applyDniData = (data, existing = {}) => {
    if (!data) return;
    const merged = { ...existing, ...Object.fromEntries(Object.entries(data).filter(([,v]) => v != null)) };
    if (merged.firstName)  setNombreSocioInput(merged.firstName);
    if (merged.lastName)   setApellidoSocioInput(merged.lastName);
    if (merged.dni)        setDniSocioInput(merged.dni);
    if (merged.birthDate)  { setFechaNacimientoSocioInput(merged.birthDate.display); setDatePickerValue(merged.birthDate.iso); }
    if (merged.expiryDate) { setCaducidadInput(merged.expiryDate.display); setDatePickerCaducidad(merged.expiryDate.iso); }
    if (merged.address)    setDireccionSocioInput(merged.address);
  };

  const processDniFile = async (file, side) => {
    if (!file) return;
    if (side === 'front') setDniFrontFile(file);
    if (side === 'back') setDniBackFile(file);
    if (id) return;
    setDniScanning(true); setDniScanError('');
    const result = await scanDni(file, side, token);
    setDniScanning(false);
    if (result.data) {
      if (side === 'front') {
        // Store front data so back scan can merge on top of it
        setDniPartial(result.data);
        applyDniData(result.data, {});
      } else {
        applyDniData(result.data, dniPartial || {});
      }
    }
    if (!result.success) setDniScanError(result.error || 'No se pudo leer la imagen.');
    else setDniScanError('');
  };

  const handleDniFrontCameraCapture = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    await processDniFile(file, 'front');
  };

  const handleDniBackCameraCapture = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    await processDniFile(file, 'back');
  };


  const openDniScanner = (step) => {
    setDniScanError('');
    setDniCameraSide(step);
    setDniCameraError('');
    setDniCameraOpen(true);
  };

  const handleDniCapturePhoto = () => {
    const video = dniVideoRef.current;
    const canvas = dniCanvasRef.current;
    if (!video || !canvas) {
      setDniCameraError('No se pudo capturar la imagen del DNI.');
      return;
    }
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(async (blob) => {
      if (!blob) {
        setDniCameraError('No se pudo generar la imagen del DNI.');
        return;
      }
      const side = dniCameraSide === 'back' ? 'back' : 'front';
      const file = new File([blob], `dni-${side}-${Date.now()}.jpg`, { type: 'image/jpeg' });
      stopStream(dniMediaStreamRef);
      setDniCameraOpen(false);
      await processDniFile(file, side);
    }, 'image/jpeg', 0.92);
  };

  // ── Member photo ───────────────────────────────────────────────────────
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setCurrentImageUrl(reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCapturePhoto = () => {
    const video = videoRef.current; const canvas = canvasRef.current;
    if (!video || !canvas) { setCameraError('No se pudo capturar la imagen.'); return; }
    canvas.width = video.videoWidth || 960; canvas.height = video.videoHeight || 720;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) { setCameraError('No se pudo generar la foto.'); return; }
      const file = new File([blob], `socio-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setImage(file); setCurrentImageUrl(URL.createObjectURL(blob));
      stopStream(mediaStreamRef); setCameraOpen(false);
    }, 'image/jpeg', 0.92);
  };

  const resolveCreateImageFile = async () => {
    if (image) return image;
    if (!currentImageUrl) return null;

    try {
      const response = await fetch(currentImageUrl);
      const blob = await response.blob();
      if (!blob || !blob.size) return null;
      return new File([blob], `socio-prefill-${Date.now()}.jpg`, {
        type: blob.type || 'image/jpeg',
      });
    } catch {
      return null;
    }
  };

  // ── Submit ─────────────────────────────────────────────────────────────
  // Delegado en el helper puro (src/lib/dates.js) para que valide de verdad: antes
  // montaba `${y}-${m}-${d}` sin comprobar nada, así que `01/01/199` se enviaba como
  // '199-01-01' y el backend lo rechazaba. Se conserva el centinela '2000-01-01'
  // porque los llamantes de abajo comparan contra él.
  const formatDate = (s) => toIsoDateOrFallback(s);

  const submitRegistration = async () => {
    setLocalError(''); setIsSubmitting(true);

    if (!id && sociosCap?.max_socios != null && sociosCap.socios_count >= sociosCap.max_socios) {
      setLocalError('Límite de socios alcanzado. Mejora tu plan para añadir más.');
      setIsSubmitting(false); return;
    }
    if (!isValidEmail(email)) {
      setLocalError('Por favor, ingresa un email válido.'); setIsSubmitting(false); return;
    }
    if (!shopId) {
      setLocalError('Error: No se pudo identificar el venue. Recarga la página.'); setIsSubmitting(false); return;
    }
    if (isPreRegistrationFlow && !caducidadInput.trim()) {
      setLocalError('Define la fecha de caducidad antes de registrar el socio.'); setIsSubmitting(false); return;
    }

    // Fecha a medio escribir (p. ej. '01/01/199' al tabular antes de acabar el año).
    // Antes se enviaba mutilada y el backend respondía un 500 opaco; ahora se avisa
    // aquí, diciendo qué campo es. Vacío sigue siendo válido: son campos opcionales.
    const fechasAmedias = [
      ['Fecha de nacimiento', fechaNacimientoSocioInput],
      ['Fecha de inscripción', inscripcionInput],
      ['Fecha de caducidad', caducidadInput],
    ].filter(([, value]) => isIncompleteDate(value));
    if (fechasAmedias.length > 0) {
      setLocalError(
        `${fechasAmedias.map(([label]) => label).join(', ')}: revisa el formato (DD/MM/AAAA).`
      );
      setIsSubmitting(false); return;
    }

    // El teléfono es OPCIONAL (muchos socios extranjeros no lo dan). Si se rellena,
    // debe ser válido para su país (E.164); vacío se acepta. PhoneInput ya entrega el
    // valor en E.164 (+34...), así que lo enviamos tal cual.
    const phoneNormalized = telefonoSocioInput || '';
    if (!isValidSocioPhone(phoneNormalized)) {
      setLocalError('Teléfono no válido. Déjalo vacío o usa formato internacional (ej. +34 600 123 456).');
      setIsSubmitting(false); return;
    }

    const imageToUpload = !id ? await resolveCreateImageFile() : image;
    if (!id && !imageToUpload) {
      setLocalError('Añade una foto del socio desde tu dispositivo o usando la cámara.'); setIsSubmitting(false); return;
    }

    const nationalId = normalizeNationalIdInput(dniSocioInput);
    const profileData = {};
    let enrollmentIsoForCallback;
    let expirationIsoForCallback;

    if (id) {
      if (nombreSocioInput) profileData.first_name = nombreSocioInput;
      if (apellidoSocioInput) profileData.last_name = apellidoSocioInput;
      if (nationalId) profileData.national_id = nationalId;
      const bd = formatDate(fechaNacimientoSocioInput);
      if (bd !== '2000-01-01') profileData.birth_date = bd;
      if (cpSocioInput) profileData.postal_code = parseInt(cpSocioInput);
      if (ciudadSocioInput) profileData.city = ciudadSocioInput;
      profileData.address = direccionSocioInput || '';
      profileData.phone = phoneNormalized;
      if (telefonoPaisInput) profileData.phone_country = telefonoPaisInput;
      const enr = formatDate(inscripcionInput);
      if (enr !== '2000-01-01') profileData.enrollment_date = enr;
      const exp = formatDate(caducidadInput);
      if (exp !== '2000-01-01') profileData.expiration_date = exp;
      if (socioAvaladoInput) profileData.guarantor_member = socioAvaladoInput;
      if (lineaCosteInput) profileData.cost_line = lineaCosteInput;
      if (descuentoInput) profileData.discount = descuentoInput;
      if (cuotaInput) profileData.fee = cuotaInput;
      if (consumoMaxInput) profileData.max_monthly_consumption = parseInt(consumoMaxInput);
      profileData.notes = notesInput.trim();
      profileData.consumption_type = { ludenos: consumptionTypeInput === 'ludenos', terap: consumptionTypeInput === 'terap' };
    } else {
      const todayIso = new Date().toISOString().split('T')[0];
      let enrollmentIso = formatDate(inscripcionInput);
      if (!inscripcionInput.trim() || enrollmentIso === '2000-01-01') enrollmentIso = todayIso;
      let expirationIso = formatDate(caducidadInput);
      if (!caducidadInput.trim() || expirationIso === '2000-01-01') {
        expirationIso = defaultExpirationAfterEnrollment(enrollmentIso);
      }
      if (expirationIso <= enrollmentIso) {
        expirationIso = defaultExpirationAfterEnrollment(enrollmentIso);
      }
      enrollmentIsoForCallback = enrollmentIso;
      expirationIsoForCallback = expirationIso;

      profileData.first_name = nombreSocioInput || 'Cliente';
      profileData.last_name  = apellidoSocioInput || 'Nuevo';
      profileData.national_id = nationalId || '12345678';
      profileData.birth_date  = formatDate(fechaNacimientoSocioInput) || '2000-01-01';
      profileData.postal_code = parseInt(cpSocioInput) || 28001;
      profileData.city        = ciudadSocioInput || 'Madrid';
      profileData.address     = direccionSocioInput || '';
      profileData.phone       = phoneNormalized;
      if (telefonoPaisInput) profileData.phone_country = telefonoPaisInput;
      profileData.enrollment_date  = enrollmentIso;
      profileData.expiration_date  = expirationIso;
      profileData.fee              = cuotaInput.trim() || 'ej: Mensual';
      profileData.cost_line        = lineaCosteInput || 'Defecto';
      profileData.discount         = descuentoInput  || 'Sin descuento';
      profileData.max_monthly_consumption = parseInt(consumoMaxInput) || 0;
      profileData.notes            = notesInput.trim();
      profileData.consumption_type = { ludenos: consumptionTypeInput === 'ludenos', terap: consumptionTypeInput === 'terap' };
      profileData.credit_balance   = 0.0;
      if (socioAvaladoInput) profileData.guarantor_member = socioAvaladoInput;
    }

    const payload = { email, profile: profileData };
    if (!id) {
      payload.password = '123456';
      payload.venue_id = shopId;
      // Optional Nº socio override (numeric). If left at the previewed value or
      // blank, the backend gap-fills. Backend returns 409 if the number is taken.
      const mid = parseInt(membershipInput, 10);
      if (!Number.isNaN(mid)) payload.membership_id = mid;
      // Firma del acta (data URL del canvas). El backend genera el acta firmada.
      if (signatureDataUrl) payload.signature = signatureDataUrl;
      // Cuota de inscripción: el backend la cobra al alta si el local tiene cuota
      // configurada y el checkbox está marcado. Se desmarca para socios que vienen
      // del Kong Club (inscripción gratuita).
      payload.charge_inscription_fee = !!(chargeInscriptionFee && inscriptionFee != null && inscriptionFee > 0);
      payload.inscription_fee_payment_method = feePaymentMethod;
    }

    const userAccess = clientData?.access_privileges;
    const priv = typeof userAccess === 'string' ? userAccess : userAccess?.value || userAccess;
    const isStaff = id && priv === 'staff';
    const isOwner = id && priv === 'owner';
    const formKey = isStaff ? 'staff_data' : isOwner ? 'owner_data' : 'client_data';
    const fileKey = isOwner ? 'image' : 'documents';

    const formData = new FormData();
    formData.append(formKey, JSON.stringify(payload));
    if (imageToUpload) formData.append(fileKey, imageToUpload);
    if (dniFrontFile) formData.append('dni_front_document', dniFrontFile);
    if (dniBackFile) formData.append('dni_back_document', dniBackFile);

    try {
      let url; const method = id ? 'put' : 'post';
      if (id) {
        url = isStaff ? `/v1/staff/${id}` : isOwner ? `/v1/owners/${id}` : `/v1/clients/${id}`;
      } else url = '/v1/clients';

      const response = await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });

      setLocalError(''); setIsSubmitting(false);

      if (!id && response.data && onClientCreated) {
        try {
          await onClientCreated(response.data, {
            enrollment_date: enrollmentIsoForCallback,
            expiration_date: expirationIsoForCallback,
          });
        } catch { /* non-blocking */ }
      }
      if (id && response.data && onClientUpdated) {
        try {
          invalidateImageCache(getImageCacheKey('client', response.data.id || id));
          await onClientUpdated(response.data);
        } catch { /* non-blocking */ }
      }

      // Reset
      setEmail(''); setImage(null);
      setNombreSocioInput(''); setApellidoSocioInput(''); setDniSocioInput('');
      setFechaNacimientoSocioInput(''); setDatePickerValue('');
      setInscripcionInput(''); setDatePickerInscripcion('');
      setCaducidadInput(''); setDatePickerCaducidad('');
      setTelefonoSocioInput(''); setTelefonoPaisInput(DEFAULT_PHONE_COUNTRY); setDireccionSocioInput('');
      setSocioAvaladoInput(''); setLineaCosteInput('Defecto');
      setDescuentoInput('Sin descuento'); setCuotaInput('');
      setConsumoMaxInput(''); setConsumptionTypeInput(null);
      setNotesInput(''); setDniFrontFile(null); setDniBackFile(null); setDniPartial(null);
      setSignatureDataUrl(null);
      handleModal();
    } catch (error) {
      setIsSubmitting(false);
      let msg = id ? 'Error al actualizar el socio' : 'Error al crear el socio';
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        msg = detail.map((e) => {
          let m = e.msg;
          if (m.includes('national_id') || m.toLowerCase().includes('dni')) m = 'El DNI ya existe.';
          else if (m.toLowerCase().includes('email')) m = 'El email ya existe.';
          return m;
        }).join('; ');
      } else if (typeof detail === 'string') {
        msg = detail.toLowerCase().includes('national_id') || detail.toLowerCase().includes('dni')
          ? 'El DNI ya existe. Verifica que no esté duplicado.'
          : detail.toLowerCase().includes('email') ? 'El email ya existe.'
          : detail.toLowerCase().includes('teléfono') || detail.toLowerCase().includes('e.164') || detail.toLowerCase().includes('phone')
          ? 'Teléfono no válido. Usa formato internacional, por ejemplo +34600123456 o +447911123456.'
          : detail;
      } else if (error.message) msg = error.message;
      setLocalError(msg);
      if (setErrorMessage) setErrorMessage(msg);
    }
  };

  const handleConsumptionTypeChange = (value) => {
    setConsumptionTypeInput(value);
  };

  const handleSubmit = (e) => { e.preventDefault(); submitRegistration(); };

  if (!active) return null;

  const atCap = !id && sociosCap?.max_socios != null && sociosCap.socios_count >= sociosCap.max_socios;
  const modalTitle = id ? 'Editar socio' : (isPreRegistrationFlow ? 'Revisar preinscripción y crear socio' : 'Crear socio');

  const modalFooter = (
    <>
      <button type="button" className="wa-btn-secondary" onClick={handleModal}>Cancelar</button>
      <button
        type="submit" form="customer-modal-form"
        disabled={isSubmitting || atCap}
        className="wa-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (id ? 'Actualizando…' : 'Creando…') : (id ? 'Actualizar' : 'Registrar')}
      </button>
    </>
  );

  return (
    <>
      <CustomModal active={active} handleModal={handleModal} title={modalTitle} size="wide" titleId="customer-modal-title" footer={modalFooter}>
        <form id="customer-modal-form" onSubmit={handleSubmit} className="wa-form">

          {localError && (
            <div className="wa-form-error mb-3" role="alert">
              <strong>Error: </strong><span>{localError}</span>
            </div>
          )}

          {!id && sociosCap && (
            <div className={`rounded-lg px-4 py-2 text-sm mb-3 ${
              atCap ? 'bg-amber-100 text-amber-800 border border-amber-300'
                    : 'bg-[var(--wa-muted)] text-[var(--wa-foreground)] border border-[var(--wa-border)]'
            }`}>
              Socios: {sociosCap.socios_count} / {sociosCap.max_socios ?? '∞'}
              {atCap && (
                <button
                  type="button"
                  onClick={() => {
                    handleModal?.();
                    navigate('/settings');
                  }}
                  className="block mt-1 font-medium underline underline-offset-2 hover:no-underline text-left"
                >
                  Límite de socios alcanzado. Ir a Configuración para mejorar el plan.
                </button>
              )}
            </div>
          )}

          {!id && (
            <div className="mb-3 max-w-[220px]">
              <label className="wa-label">Nº socio</label>
              <input
                type="number"
                value={membershipInput}
                onChange={(e) => setMembershipInput(e.target.value)}
                placeholder={nextMembershipId != null ? String(nextMembershipId) : 'auto'}
                className="wa-input"
              />
              <p className="mt-1 text-[11px] text-[var(--wa-muted-foreground)]">
                {nextMembershipId != null
                  ? `Siguiente disponible: ${nextMembershipId}. Puedes cambiarlo.`
                  : 'Se asignará automáticamente.'}
              </p>
            </div>
          )}

          {id && clientData && (
            <div className="rounded-lg border border-[var(--wa-border)] bg-[var(--wa-muted)] px-4 py-2 text-sm mb-3">
              <span className="text-[var(--wa-muted-foreground)]">Saldo actual: </span>
              <span className="font-semibold text-[var(--wa-foreground)]">€{(Number(clientData.profile?.credit_balance) || 0).toFixed(2)}</span>
              <span className="block text-xs text-[var(--wa-muted-foreground)] mt-0.5">Usa «Añadir crédito» para modificar el saldo</span>
            </div>
          )}

          {/* ── DNI scanner status ── */}
          {dniScanning && (
            <div className="flex items-center gap-2 rounded-lg bg-lime-50 border border-lime-200 px-4 py-2 text-sm text-lime-800 mb-3">
              <svg className="animate-spin w-4 h-4 text-lime-600 shrink-0" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Leyendo DNI… esto puede tardar unos segundos.
            </div>
          )}
          {dniScanError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700 mb-3 flex items-start justify-between gap-2">
              <span>{dniScanError}</span>
              <button type="button" onClick={() => setDniScanError('')} className="text-red-400 hover:text-red-600 shrink-0">✕</button>
            </div>
          )}

          {id ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">

            {/* ── LEFT: Datos principales ─────────────────────────────── */}
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Datos principales</h3>

                {/* Scan / Upload DNI (create mode only) */}
                {!id && (
                  <div className="flex flex-wrap gap-1.5">
                    <button type="button" onClick={() => openDniScanner('front')}
                      className="flex items-center gap-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-2.5 py-1.5"
                      title="Cámara — anverso">
                      📷 Anverso
                    </button>
                    <button type="button" onClick={() => openDniScanner('back')}
                      className="flex items-center gap-1 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium px-2.5 py-1.5"
                      title="Cámara — reverso (MRZ)">
                      📷 Reverso
                    </button>
                  </div>
                )}
              </div>

              {/* Nombre + Apellidos */}
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Nombre</label>
                  <input value={nombreSocioInput} onChange={(e) => setNombreSocioInput(e.target.value)} placeholder="nombre" className="wa-input" />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Apellidos</label>
                  <input value={apellidoSocioInput} onChange={(e) => setApellidoSocioInput(e.target.value)} placeholder="apellidos" className="wa-input" />
                </div>
              </div>

              {/* DNI + Fecha nacimiento */}
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">DNI / NIE / Pasaporte</label>
                  <input
                    value={dniSocioInput}
                    onChange={(e) => setDniSocioInput(e.target.value)}
                    placeholder="12345678A, X1234567A, pasaporte…"
                    maxLength={20}
                    className="wa-input"
                  />
                </div>
                <DateField
                  label="Fecha nacimiento"
                  value={fechaNacimientoSocioInput}
                  onChange={setFechaNacimientoSocioInput}
                  pickerValue={datePickerValue}
                  onPickerChange={setDatePickerValue}
                />
              </div>

              {/* Teléfono + Email */}
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Teléfono</label>
                  <PhoneInput
                    international
                    defaultCountry={DEFAULT_PHONE_COUNTRY}
                    value={telefonoSocioInput || undefined}
                    onChange={(value) => setTelefonoSocioInput(value || '')}
                    onCountryChange={(c) => setTelefonoPaisInput(c || '')}
                    placeholder="Teléfono"
                    className="wa-phone-input"
                  />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email" className="wa-input" required />
                </div>
              </div>

              {/* Dirección */}
              <div className="wa-form-field">
                <label className="wa-label">Dirección</label>
                <input value={direccionSocioInput} onChange={(e) => setDireccionSocioInput(e.target.value)} placeholder="dirección" className="wa-input" />
              </div>

              {/* Duración / Fechas inscripción (edición: mismos atajos que en el alta completa) */}
              <div className="space-y-3">
                <div className="wa-form-field">
                  <label className="wa-label">Duración de inscripción</label>
                  <div className="flex flex-wrap gap-2">
                    {durations.map((d) => {
                      const isActive = selectedDurationId === d.id;
                      return (
                        <button
                          key={d.id} type="button"
                          onClick={() => {
                            const today = new Date();
                            const expiry = addMonths(today, d.months);
                            setSelectedDurationId(d.id);
                            setInscripcionInput(toDisplay(today));   setDatePickerInscripcion(toISO(today));
                            setCaducidadInput(toDisplay(expiry));    setDatePickerCaducidad(toISO(expiry));
                          }}
                          className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                            isActive ? 'bg-lime-500 text-white border-lime-500 shadow-sm'
                                     : 'bg-white text-gray-700 border-gray-300 hover:border-lime-400 hover:text-lime-700'
                          }`}
                        >
                          {d.label}
                        </button>
                      );
                    })}
                  </div>
                  {selectedDurationId && inscripcionInput && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      Inscripción: <span className="font-medium text-gray-700">{inscripcionInput}</span>
                      {' · '}Caducidad: <span className="font-medium text-gray-700">{caducidadInput}</span>
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <DateField label="Inscripción" value={inscripcionInput} onChange={setInscripcionInput} pickerValue={datePickerInscripcion} onPickerChange={setDatePickerInscripcion} />
                  <DateField label="Caducidad"   value={caducidadInput}   onChange={setCaducidadInput}   pickerValue={datePickerCaducidad}   onPickerChange={setDatePickerCaducidad} />
                </div>
              </div>

              {/* Documentos DNI */}
              <div className="wa-form-field">
                <span className="wa-label">Documentos DNI</span>
                <div className="flex flex-wrap gap-3 mb-2">
                  {(dniNewFrontPreviewUrl || dniExistingFrontUrl) ? (
                    <div className="flex flex-col items-center gap-1">
                      <img
                        src={dniNewFrontPreviewUrl || dniExistingFrontUrl}
                        alt="DNI anverso"
                        className="h-20 rounded-lg border border-[var(--wa-border)] object-cover cursor-pointer"
                        onClick={() => window.open(dniNewFrontPreviewUrl || dniExistingFrontUrl, '_blank')}
                        title="Ver anverso completo"
                      />
                      <span className="text-xs text-[var(--wa-muted-foreground)]">
                        Anverso{dniNewFrontPreviewUrl ? ' (nuevo)' : ''}
                      </span>
                    </div>
                  ) : null}
                  {(dniNewBackPreviewUrl || dniExistingBackUrl) ? (
                    <div className="flex flex-col items-center gap-1">
                      <img
                        src={dniNewBackPreviewUrl || dniExistingBackUrl}
                        alt="DNI reverso"
                        className="h-20 rounded-lg border border-[var(--wa-border)] object-cover cursor-pointer"
                        onClick={() => window.open(dniNewBackPreviewUrl || dniExistingBackUrl, '_blank')}
                        title="Ver reverso completo"
                      />
                      <span className="text-xs text-[var(--wa-muted-foreground)]">
                        Reverso{dniNewBackPreviewUrl ? ' (nuevo)' : ''}
                      </span>
                    </div>
                  ) : null}
                  {!dniNewFrontPreviewUrl && !dniExistingFrontUrl && !dniNewBackPreviewUrl && !dniExistingBackUrl && (
                    <span className="text-xs text-[var(--wa-muted-foreground)]">Sin escaneos de DNI</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => openDniScanner('front')}
                    className="flex items-center gap-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-2.5 py-1.5"
                    title="Cámara — anverso">
                    Cámara anverso
                  </button>
                  <button type="button" onClick={() => openDniScanner('back')}
                    className="flex items-center gap-1 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium px-2.5 py-1.5"
                    title="Cámara — reverso">
                    Cámara reverso
                  </button>
                  <button type="button" onClick={() => dniGalleryFrontRef.current?.click()}
                    className="wa-btn-secondary h-9 text-sm px-3">
                    Subir anverso
                  </button>
                  <button type="button" onClick={() => dniGalleryBackRef.current?.click()}
                    className="wa-btn-secondary h-9 text-sm px-3">
                    Subir reverso
                  </button>
                </div>
                <p className="text-xs text-[var(--wa-muted-foreground)] mt-1">
                  Sube nuevas imágenes solo si quieres reemplazar los escaneos actuales.
                </p>
              </div>

              {/* Foto */}
              <div className="wa-form-field">
                <label className="wa-label">{id ? 'Cambiar foto (opcional)' : 'Foto del socio'}</label>
                {currentImageUrl && (
                  <div className="mb-2">
                    <img src={currentImageUrl} alt="Foto socio" className="w-16 h-16 rounded-full object-cover border border-[var(--wa-border)]" />
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => galleryInputRef.current?.click()} className="wa-btn-secondary h-9 text-sm px-3">
                    Desde dispositivo
                  </button>
                  <button type="button" onClick={() => setCameraOpen(true)} className="wa-btn-primary h-9 text-sm px-3">
                    Hacer foto
                  </button>
                </div>
                <input ref={galleryInputRef}  type="file" accept="image/*"          onChange={handleImageChange} className="hidden" />
                <input ref={cameraInputRef}   type="file" accept="image/*" capture="user" onChange={handleImageChange} className="hidden" />
                <p className="text-xs text-[var(--wa-muted-foreground)] mt-1">
                  {id ? 'Deja vacío para mantener la foto actual.' : 'Requerida para crear el socio.'}
                </p>
              </div>
            </div>

            {/* ── RIGHT: Datos adicionales ─────────────────────────────── */}
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Datos adicionales</h3>

              {/* Socio avalado + Línea de coste */}
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Socio avalado</label>
                  <input value={socioAvaladoInput} onChange={(e) => setSocioAvaladoInput(e.target.value)} placeholder="socio avalado" className="wa-input" />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Línea de coste</label>
                  <select value={lineaCosteInput} onChange={(e) => setLineaCosteInput(e.target.value)} className="wa-select">
                    <option>Defecto</option><option>Segunda</option><option>Tercera</option>
                  </select>
                </div>
              </div>

              {/* CP + Ciudad */}
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">CP</label>
                  <input value={cpSocioInput} onChange={(e) => setCpSocioInput(e.target.value)} placeholder="cp" className="wa-input" />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Ciudad</label>
                  <input value={ciudadSocioInput} onChange={(e) => setCiudadSocioInput(e.target.value)} placeholder="ciudad" className="wa-input" />
                </div>
              </div>

              {/* Descuento + Cuota */}
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Descuento</label>
                  <select value={descuentoInput} onChange={(e) => setDescuentoInput(e.target.value)} className="wa-select">
                    <option>Sin descuento</option>
                    <option>5%</option><option>10%</option><option>15%</option>
                    <option>20%</option><option>50%</option><option>100%</option>
                  </select>
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Cuota</label>
                  <input type="text" value={cuotaInput} onChange={(e) => setCuotaInput(e.target.value)} placeholder="ej: Mensual" className="wa-input" />
                </div>
              </div>

              {/* Consumo + Tipo */}
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Consumo máx/mes</label>
                  <input type="number" value={consumoMaxInput} onChange={(e) => setConsumoMaxInput(e.target.value)} placeholder="0" min="0" className="wa-input" />
                </div>
                <div className="wa-form-field">
                  <span className="wa-label">Tipo de consumo</span>
                  <div className="flex flex-wrap gap-4 items-center min-h-[40px]">
                    <label className="flex items-center gap-2 text-sm text-[var(--wa-foreground)] cursor-pointer">
                      <input type="radio" name="consumption_type" value="ludenos" checked={consumptionTypeInput === 'ludenos'} onChange={(e) => handleConsumptionTypeChange(e.target.value)} />
                      Lúd.
                    </label>
                    <label className="flex items-center gap-2 text-sm text-[var(--wa-foreground)] cursor-pointer">
                      <input type="radio" name="consumption_type" value="terap" checked={consumptionTypeInput === 'terap'} onChange={(e) => handleConsumptionTypeChange(e.target.value)} />
                      Terap.
                    </label>
                  </div>
                </div>
              </div>

              {/* Documentos */}
              <div className="wa-form-field">
                <label className="wa-label">Notas</label>
                <textarea
                  value={notesInput}
                  onChange={(e) => setNotesInput(e.target.value)}
                  className="wa-textarea wa-textarea-compact"
                  rows={5}
                  placeholder="Observaciones internas sobre este socio"
                />
              </div>
            </div>
          </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">

            {/* DATOS PERSONALES */}
            <section className="rounded-2xl border border-gray-200 p-4 space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Datos personales</h3>

              {/* Foto del socio (arriba, como Figma) */}
              <div className="wa-form-field">
                <label className="wa-label">Foto del socio</label>
                {currentImageUrl && (
                  <div className="mb-2">
                    <img src={currentImageUrl} alt="Foto socio" className="w-16 h-16 rounded-full object-cover border border-[var(--wa-border)]" />
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => setCameraOpen(true)} className="wa-btn-primary h-9 text-sm px-3">
                    Hacer foto
                  </button>
                  <button type="button" onClick={() => galleryInputRef.current?.click()} className="wa-btn-secondary h-9 text-sm px-3">
                    Desde dispositivo
                  </button>
                </div>
                <input ref={galleryInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                <input ref={cameraInputRef} type="file" accept="image/*" capture="user" onChange={handleImageChange} className="hidden" />
                <p className="text-xs text-[var(--wa-muted-foreground)] mt-1">Requerida para crear el socio.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Nombre</label>
                  <input value={nombreSocioInput} onChange={(e) => setNombreSocioInput(e.target.value)} placeholder="nombre" className="wa-input" />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Apellidos</label>
                  <input value={apellidoSocioInput} onChange={(e) => setApellidoSocioInput(e.target.value)} placeholder="apellidos" className="wa-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">DNI / NIE / Pasaporte</label>
                  <input
                    value={dniSocioInput}
                    onChange={(e) => setDniSocioInput(e.target.value)}
                    placeholder="12345678A, X1234567A, pasaporte…"
                    maxLength={20}
                    className="wa-input"
                  />
                </div>
                <DateField
                  label="Fecha nacimiento"
                  value={fechaNacimientoSocioInput}
                  onChange={setFechaNacimientoSocioInput}
                  pickerValue={datePickerValue}
                  onPickerChange={setDatePickerValue}
                />
              </div>
            </section>

            {/* MEMBRESÍA */}
            <section className="rounded-2xl border border-gray-200 p-4 space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Membresía</h3>

              <div className="wa-form-field">
                <label className="wa-label">Socio avalado</label>
                <input value={socioAvaladoInput} onChange={(e) => setSocioAvaladoInput(e.target.value)} placeholder="socio avalado" className="wa-input" />
              </div>

              <div className="wa-form-field">
                <label className="wa-label">Duración de inscripción</label>
                <div className="flex flex-wrap gap-2">
                  {durations.map((d) => {
                    const isActive = selectedDurationId === d.id;
                    return (
                      <button
                        key={d.id} type="button"
                        onClick={() => {
                          const today = new Date();
                          const expiry = addMonths(today, d.months);
                          setSelectedDurationId(d.id);
                          setInscripcionInput(toDisplay(today));   setDatePickerInscripcion(toISO(today));
                          setCaducidadInput(toDisplay(expiry));    setDatePickerCaducidad(toISO(expiry));
                        }}
                        className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                          isActive ? 'bg-lime-500 text-white border-lime-500 shadow-sm'
                                   : 'bg-white text-gray-700 border-gray-300 hover:border-lime-400 hover:text-lime-700'
                        }`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
                {selectedDurationId && inscripcionInput && (
                  <p className="mt-1.5 text-xs text-gray-500">
                    Inscripción: <span className="font-medium text-gray-700">{inscripcionInput}</span>
                    {' · '}Caducidad: <span className="font-medium text-gray-700">{caducidadInput}</span>
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <DateField label="Inscripción" value={inscripcionInput} onChange={setInscripcionInput} pickerValue={datePickerInscripcion} onPickerChange={setDatePickerInscripcion} />
                <DateField label="Caducidad"   value={caducidadInput}   onChange={setCaducidadInput}   pickerValue={datePickerCaducidad}   onPickerChange={setDatePickerCaducidad} />
              </div>

              {inscriptionFee != null && inscriptionFee > 0 && (
                <div className="wa-form-field pt-1">
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={chargeInscriptionFee}
                      onChange={(e) => setChargeInscriptionFee(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-lime-600 focus:ring-lime-500"
                    />
                    <span className="text-sm text-gray-700">
                      Cobrar cuota de inscripción (<span className="font-semibold">{inscriptionFee.toFixed(2)} €</span>)
                      <span className="block text-xs text-gray-400">Desmárcalo para socios que vienen del Kong Club (inscripción gratuita).</span>
                    </span>
                  </label>
                  {chargeInscriptionFee && (
                    <div className="mt-2 flex gap-2">
                      {/* La cuota de inscripción se cobra siempre en efectivo (va a la caja). */}
                      <span className="px-3 py-1.5 rounded-lg text-xs font-medium border bg-lime-500 text-white border-lime-500">
                        Efectivo
                      </span>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* CONTACTO */}
            <section className="rounded-2xl border border-gray-200 p-4 space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Contacto</h3>

              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Teléfono</label>
                  <PhoneInput
                    international
                    defaultCountry={DEFAULT_PHONE_COUNTRY}
                    value={telefonoSocioInput || undefined}
                    onChange={(value) => setTelefonoSocioInput(value || '')}
                    onCountryChange={(c) => setTelefonoPaisInput(c || '')}
                    placeholder="Teléfono"
                    className="wa-phone-input"
                  />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email" className="wa-input" required />
                </div>
              </div>

              <div className="wa-form-field">
                <label className="wa-label">Dirección</label>
                <input value={direccionSocioInput} onChange={(e) => setDireccionSocioInput(e.target.value)} placeholder="dirección" className="wa-input" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">CP</label>
                  <input value={cpSocioInput} onChange={(e) => setCpSocioInput(e.target.value)} placeholder="cp" className="wa-input" />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Ciudad</label>
                  <input value={ciudadSocioInput} onChange={(e) => setCiudadSocioInput(e.target.value)} placeholder="ciudad" className="wa-input" />
                </div>
              </div>
            </section>

            {/* DOCUMENTACIÓN (DNI) */}
            <section className="rounded-2xl border border-gray-200 p-4 space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Documentación (DNI)</h3>

              {(dniPrefillFrontUrl || dniPrefillBackUrl) && (
                <div className="wa-form-field">
                  <span className="wa-label">DNI preinscripción</span>
                  <div className="flex flex-wrap gap-3">
                    {dniPrefillFrontUrl && (
                      <div className="flex flex-col items-center gap-1">
                        <img src={dniPrefillFrontUrl} alt="DNI anverso" className="h-20 rounded-lg border border-[var(--wa-border)] object-cover cursor-pointer"
                          onClick={() => window.open(dniPrefillFrontUrl, '_blank')} title="Ver anverso completo" />
                        <span className="text-xs text-[var(--wa-muted-foreground)]">Anverso</span>
                      </div>
                    )}
                    {dniPrefillBackUrl && (
                      <div className="flex flex-col items-center gap-1">
                        <img src={dniPrefillBackUrl} alt="DNI reverso" className="h-20 rounded-lg border border-[var(--wa-border)] object-cover cursor-pointer"
                          onClick={() => window.open(dniPrefillBackUrl, '_blank')} title="Ver reverso completo" />
                        <span className="text-xs text-[var(--wa-muted-foreground)]">Reverso</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="wa-form-field">
                <span className="wa-label">DNI — cámara</span>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => openDniScanner('front')}
                    className="flex items-center gap-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-2.5 py-1.5"
                    title="Cámara — anverso">
                    Anverso
                  </button>
                  <button type="button" onClick={() => openDniScanner('back')}
                    className="flex items-center gap-1 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium px-2.5 py-1.5"
                    title="Cámara — reverso (MRZ)">
                    Reverso
                  </button>
                </div>
              </div>

              <div className="wa-form-field">
                <span className="wa-label">DNI — desde dispositivo (imagen)</span>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => dniGalleryFrontRef.current?.click()}
                    className="wa-btn-secondary h-9 text-sm px-3">
                    Anverso
                  </button>
                  <button type="button" onClick={() => dniGalleryBackRef.current?.click()}
                    className="wa-btn-secondary h-9 text-sm px-3">
                    Reverso
                  </button>
                </div>
              </div>
            </section>
          </div>
          )}

          {/* ── Acta de inscripción (firma al crear · ver al editar) ── */}
          {!id ? (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Firma del acta de inscripción</h3>
              <p className="text-xs text-gray-500 mb-2">El socio firma con el dedo; se genera y guarda el acta firmada al registrar.</p>
              <SignaturePad value={signatureDataUrl} onChange={setSignatureDataUrl} />
            </div>
          ) : (clientData?.profile?.inscription_document_path ? (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Acta de inscripción</h3>
              <button
                type="button"
                className="wa-btn-secondary h-9 text-sm px-3"
                onClick={async () => {
                  try {
                    const res = await fetch(`/v1/clients/${id}/acta`, { headers: { Authorization: `Bearer ${token}` } });
                    if (!res.ok) { alert('No se pudo cargar el acta'); return; }
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank');
                  } catch { alert('Error al abrir el acta'); }
                }}
              >
                📄 Ver acta firmada
              </button>
            </div>
          ) : null)}
        </form>
      </CustomModal>

      {/* ── Member photo camera overlay ────────────────────────────────── */}
      {cameraOpen && (
        <div className="fixed inset-0 z-[100001] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-4 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Foto del socio</h3>
                <p className="text-sm text-gray-500">Coloca al socio frente a la cámara.</p>
              </div>
              <button type="button" onClick={() => { stopStream(mediaStreamRef); setCameraOpen(false); }} className="rounded-full px-3 py-1 text-2xl text-gray-500 hover:bg-gray-100">×</button>
            </div>
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-950">
              <video ref={videoRef} autoPlay playsInline muted className="max-h-[55vh] w-full object-cover" />
              <canvas ref={canvasRef} className="hidden" />
            </div>
            {cameraError && <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">{cameraError}</div>}
            <div className="mt-4 flex flex-wrap justify-end gap-3">
              <button type="button" onClick={() => cameraInputRef.current?.click()} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Selector dispositivo</button>
              <button type="button" onClick={() => { stopStream(mediaStreamRef); setCameraOpen(false); }} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancelar</button>
              <button type="button" onClick={handleCapturePhoto} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Capturar foto</button>
            </div>
          </div>
        </div>
      )}

      {/* ── DNI camera overlay ────────────────────────────────────────── */}
      {dniCameraOpen && (
        <div className="fixed inset-0 z-[100001] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-4 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {dniCameraSide === 'back' ? 'Escanear reverso del DNI' : 'Escanear anverso del DNI'}
                </h3>
                <p className="text-sm text-gray-500">
                  {dniCameraSide === 'back'
                    ? 'Coloca la parte trasera del DNI dentro del encuadre.'
                    : 'Coloca la parte delantera del DNI dentro del encuadre.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { stopStream(dniMediaStreamRef); setDniCameraOpen(false); }}
                className="rounded-full px-3 py-1 text-2xl text-gray-500 hover:bg-gray-100"
              >
                ×
              </button>
            </div>
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-950">
              <video ref={dniVideoRef} autoPlay playsInline muted className="max-h-[55vh] w-full object-cover" />
              <canvas ref={dniCanvasRef} className="hidden" />
            </div>
            {dniCameraError && (
              <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {dniCameraError}
              </div>
            )}
            <div className="mt-4 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  if (dniCameraSide === 'front') dniCamFrontInputRef.current?.click();
                  else dniCamBackInputRef.current?.click();
                }}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Usar archivo
              </button>
              <button
                type="button"
                onClick={() => { stopStream(dniMediaStreamRef); setDniCameraOpen(false); }}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDniCapturePhoto}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Capturar {dniCameraSide === 'back' ? 'reverso' : 'anverso'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DNI: cámara (capture) y galería (sin capture) ── */}
      <input ref={dniCamFrontInputRef} type="file" accept="image/*" capture="environment" onChange={handleDniFrontCameraCapture} className="hidden" />
      <input ref={dniCamBackInputRef} type="file" accept="image/*" capture="environment" onChange={handleDniBackCameraCapture} className="hidden" />
      <input ref={dniGalleryFrontRef} type="file" accept="image/*" onChange={handleDniFrontCameraCapture} className="hidden" />
      <input ref={dniGalleryBackRef} type="file" accept="image/*" onChange={handleDniBackCameraCapture} className="hidden" />
    </>
  );
}

export default CustomerModal;
